// SNIGJ - Jurisdiction Share Modal Component
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { Share2, X, Globe, Building2, Trash2, Clock, Shield } from 'lucide-react';
import { toast } from 'sonner';

const JurisdictionShareModal = ({ isOpen, onClose, resourceType, resourceId, resourceTitle }) => {
  const { language, user } = useAuth();
  const [provinces, setProvinces] = useState([]);
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedProvince, setSelectedProvince] = useState('');
  const [reason, setReason] = useState('');
  const [isPermanent, setIsPermanent] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadProvinces();
      loadShares();
    }
  }, [isOpen]);

  const loadProvinces = async () => {
    try {
      const res = await api.get('/jurisdictions/provinces');
      setProvinces(res.data.provinces || []);
    } catch (err) {
      console.error('Failed to load provinces:', err);
    }
  };

  const loadShares = async () => {
    try {
      const res = await api.get('/jurisdiction-shares', { params: { resource_type: resourceType, resource_id: resourceId } });
      setShares(res.data.shares || []);
    } catch (err) {
      console.error('Failed to load shares:', err);
    }
  };

  const handleShare = async () => {
    if (!selectedProvince) {
      toast.error(language === 'fr' ? 'Sélectionnez une province' : 'Select a province');
      return;
    }
    setLoading(true);
    try {
      await api.post('/jurisdiction-shares', {
        resource_type: resourceType,
        resource_id: resourceId,
        shared_with_jurisdiction: selectedProvince,
        share_type: 'PROVINCE',
        reason,
        is_permanent: isPermanent,
        permissions: ['VIEW']
      });
      toast.success(language === 'fr' ? 'Dossier partagé avec succès' : 'Successfully shared');
      setSelectedProvince('');
      setReason('');
      loadShares();
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'fr' ? 'Erreur de partage' : 'Share failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = async (shareId) => {
    try {
      await api.delete(`/jurisdiction-shares/${shareId}`);
      toast.success(language === 'fr' ? 'Partage révoqué' : 'Share revoked');
      loadShares();
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'fr' ? 'Erreur' : 'Error'));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="jurisdiction-share-modal">
      <div className="bg-card rounded-lg shadow-xl w-full max-w-lg max-h-[85vh] overflow-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <Share2 className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-semibold">
              {language === 'fr' ? 'Partager avec une juridiction' : 'Share with jurisdiction'}
            </h2>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Resource info */}
        <div className="px-4 pt-3">
          <p className="text-sm text-muted-foreground">
            {resourceType === 'CASE' ? (language === 'fr' ? 'Dossier' : 'Case') :
             resourceType === 'PRISONER' ? (language === 'fr' ? 'Détenu' : 'Prisoner') :
             (language === 'fr' ? 'Procès' : 'Trial')}: <strong>{resourceTitle}</strong>
          </p>
        </div>

        {/* Share form */}
        <div className="p-4 space-y-4">
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Globe className="w-4 h-4" />
              {language === 'fr' ? 'Province destinataire' : 'Target Province'}
            </Label>
            <Select value={selectedProvince} onValueChange={setSelectedProvince}>
              <SelectTrigger data-testid="share-province-select">
                <SelectValue placeholder={language === 'fr' ? 'Sélectionner une province...' : 'Select province...'} />
              </SelectTrigger>
              <SelectContent>
                {provinces.filter(p => p !== user?.province).map(p => (
                  <SelectItem key={p} value={p}>{p}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>{language === 'fr' ? 'Motif du partage' : 'Reason'}</Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={language === 'fr' ? 'Ex: Transfert de compétence, enquête conjointe...' : 'Ex: Jurisdiction transfer, joint investigation...'}
              rows={2}
              data-testid="share-reason-input"
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="permanent"
              checked={isPermanent}
              onChange={(e) => setIsPermanent(e.target.checked)}
              className="rounded"
            />
            <Label htmlFor="permanent" className="text-sm cursor-pointer">
              {language === 'fr' ? 'Partage permanent' : 'Permanent share'}
            </Label>
          </div>

          <Button onClick={handleShare} disabled={loading || !selectedProvince} className="w-full gap-2" data-testid="share-submit-btn">
            <Share2 className="w-4 h-4" />
            {loading ? (language === 'fr' ? 'Partage...' : 'Sharing...') : (language === 'fr' ? 'Partager' : 'Share')}
          </Button>
        </div>

        {/* Existing shares */}
        {shares.length > 0 && (
          <div className="px-4 pb-4 space-y-2">
            <h3 className="text-sm font-semibold flex items-center gap-2">
              <Shield className="w-4 h-4" />
              {language === 'fr' ? 'Partages actifs' : 'Active shares'}
            </h3>
            {shares.map((share) => (
              <Card key={share.share_id} className="border-l-4 border-l-primary/50">
                <CardContent className="p-3 flex items-center justify-between">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">{share.shared_with_jurisdiction}</Badge>
                      {share.is_permanent && <Badge className="text-xs bg-green-100 text-green-700">{language === 'fr' ? 'Permanent' : 'Permanent'}</Badge>}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 truncate">
                      {share.reason || (language === 'fr' ? 'Aucun motif' : 'No reason')}
                    </p>
                    <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                      <Clock className="w-3 h-3" />
                      {language === 'fr' ? 'Par' : 'By'} {share.shared_by_name} - {new Date(share.created_at).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => handleRevoke(share.share_id)} className="text-red-600 shrink-0" data-testid={`revoke-share-${share.share_id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default JurisdictionShareModal;
